import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { getMe } from '@/services/api'
import { TERMS_GATE_PATH } from '@/components/RequireAuth'

export function AuthCallbackPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { setUser } = useAuthStore()

    useEffect(() => {
        const errorParam = searchParams.get('error')

        if (errorParam) {
            const provider = searchParams.get('provider')
            const message =
                provider === 'facebook'
                    ? t('auth.facebookLoginCancelled')
                    : t('auth.loginCancelled', { error: errorParam })
            toast.error(message)
            navigate('/login', { replace: true })
            return
        }

        getMe()
            .then((user) => {
                setUser(user)
                const returnUrl = sessionStorage.getItem('auth-return-url') || '/'
                sessionStorage.removeItem('auth-return-url')
                // Most return targets are public pages outside RequireAuth, so the
                // gate has to be entered from here for social logins.
                if (user.terms_acceptance_required) {
                    navigate(`${TERMS_GATE_PATH}?returnUrl=${encodeURIComponent(returnUrl)}`, {
                        replace: true,
                    })
                    return
                }
                navigate(returnUrl, { replace: true })
            })
            .catch(() => {
                toast.error(t('auth.loginCallbackFailed'))
                navigate('/login', { replace: true })
            })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
                <svg
                    className="animate-spin h-8 w-8 text-secondary"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    />
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                </svg>
                <p className="text-muted-foreground text-sm">
                    {t('common.authenticating')}
                </p>
            </div>
        </div>
    )
}
